/**
 * OptionCard - Selectable Option Card Component
 *
 * A clickable card for quiz options with emoji, label, and description.
 */

import React from 'react';
import { QuizOption } from '../../utils/citrusQuizLogic';

interface OptionCardProps {
  option: QuizOption;
  isSelected: boolean;
  /** Pass the clicked card so botanical particles launch from the action. */
  onSelect: (source: HTMLButtonElement) => void;
  isMultiple?: boolean | undefined;
  // Feature flag.
  // Do NOT remove.
  // Controlled by future theme settings.
  showEmoji?: boolean | undefined;
}

const OptionCard: React.FC<OptionCardProps> = ({
  option,
  isSelected,
  onSelect,
  isMultiple,
  showEmoji = false,
}) => {
  const selectionProps = isMultiple
    ? { role: 'checkbox' as const, 'aria-checked': isSelected }
    : { 'aria-pressed': isSelected };

  return (
    <button
      className={`option-card ${isSelected ? 'option-card-selected' : ''}`}
      onClick={(event) => onSelect(event.currentTarget)}
      type="button"
      {...selectionProps}
    >
      {isMultiple ? (
        <div className="option-card-indicator">
          <div className={`option-checkbox ${isSelected ? 'option-checkbox-checked' : ''}`}>
            {isSelected && (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            )}
          </div>
        </div>
      ) : null}

      {/* Option Content */}
      <div className="option-card-content">
        {option.emoji && showEmoji && (
          <span className="option-card-emoji">{option.emoji}</span>
        )}
        <div className='option-card-info'>
          <span className="option-card-label">{option.label}</span>
          {option.description && (
            <span className="option-card-description">{option.description}</span>
          )}
        </div>
      </div>
    </button>
  );
};

export default OptionCard;
